import React, { useState, useEffect, useMemo } from 'react';
import { BookOpen, X, ChevronDown, ChevronRight } from 'lucide-react';
import { keywordService, KeywordDefinition } from '../services/keywordService';
import { marked } from 'marked';
import { cn } from '../utils/cn';

interface TechnicalTermsSidebarProps {
  content: string;
  className?: string;
  isOpen?: boolean;
  onToggle?: () => void;
  highlightingEnabled?: boolean;
  onToggleHighlighting?: (enabled: boolean) => void;
}

interface TermWithContext {
  definition: KeywordDefinition;
  context: string;
  frequency: number;
}

const TechnicalTermsSidebar: React.FC<TechnicalTermsSidebarProps> = ({
  content,
  className = '',
  isOpen = false,
  onToggle,
  highlightingEnabled = true,
  onToggleHighlighting
}) => {
  const [terms, setTerms] = useState<TermWithContext[]>([]);
  const [expandedTerms, setExpandedTerms] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');

  // Extract technical terms from content
  useEffect(() => {
    if (!content) {
      setTerms([]);
      return;
    }

    // Clean the content (remove markdown)
    const cleanContent = marked(content, { breaks: true, gfm: true })
      .replace(/<[^>]*>/g, '') // Remove HTML tags
      .replace(/[^\w\s]/g, ' ') // Remove punctuation
      .replace(/\s+/g, ' ') // Normalize whitespace
      .trim();

    // Find keywords in the content
    const foundKeywords = keywordService.findKeywordsInText(cleanContent);
    
    // Group by keyword and count frequency
    const termMap = new Map<string, TermWithContext>();
    
    foundKeywords.forEach(({ word, definition, startIndex, endIndex }) => {
      const key = definition.id;
      
      if (termMap.has(key)) {
        const existing = termMap.get(key)!;
        existing.frequency += 1;
        
        // Add more context if this occurrence is different
        const context = cleanContent.substring(
          Math.max(0, startIndex - 50),
          Math.min(cleanContent.length, endIndex + 50)
        ).trim();
        
        if (!existing.context.includes(context)) {
          existing.context += ' ... ' + context;
        }
      } else {
        const context = cleanContent.substring(
          Math.max(0, startIndex - 50),
          Math.min(cleanContent.length, endIndex + 50)
        ).trim();
        
        termMap.set(key, {
          definition,
          context,
          frequency: 1
        });
      }
    });

    // Convert to array and sort by frequency
    const sortedTerms = Array.from(termMap.values())
      .sort((a, b) => b.frequency - a.frequency);

    setTerms(sortedTerms);
  }, [content]);

  // Filter terms based on search query
  const filteredTerms = useMemo(() => {
    if (!searchQuery.trim()) return terms;
    
    const query = searchQuery.toLowerCase();
    return terms.filter(term => 
      term.definition.word.toLowerCase().includes(query) ||
      term.definition.explanation.toLowerCase().includes(query) ||
      term.definition.category?.toLowerCase().includes(query)
    );
  }, [terms, searchQuery]);

  const toggleTermExpansion = (termId: string) => {
    const newExpanded = new Set(expandedTerms);
    if (newExpanded.has(termId)) {
      newExpanded.delete(termId);
    } else {
      newExpanded.add(termId);
    }
    setExpandedTerms(newExpanded);
  };

  const getCategoryColor = (category?: string) => {
    const colors: { [key: string]: string } = {
      'Technical': 'bg-blue-100 text-blue-800',
      'System': 'bg-green-100 text-green-800',
      'Manufacturing': 'bg-purple-100 text-purple-800',
      'Sales': 'bg-orange-100 text-orange-800',
      'Tool': 'bg-pink-100 text-pink-800'
    };
    return colors[category || ''] || 'bg-gray-100 text-gray-800';
  };

  if (!isOpen) {
    return (
      <div className={`w-12 bg-white border-l border-gray-200 flex flex-col items-center py-4 ${className}`}>
        <button
          onClick={onToggle}
          className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
          title="Show Technical Terms"
        >
          <BookOpen className="w-5 h-5" />
        </button>
      </div>
    );
  }

  return (
    <div className={`w-64 bg-white border-l border-gray-200 flex flex-col ${className}`}>
      {/* Header */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center space-x-2">
            <BookOpen className="w-5 h-5 text-blue-600" />
            <h3 className="font-semibold text-gray-900">Technical Terms</h3>
          </div>
          <button
            onClick={onToggle}
            className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        
        {/* Highlighting Toggle */}
        {onToggleHighlighting && (
          <div className="mb-3 flex items-center justify-between p-2 bg-gray-50 rounded-lg">
            <span className="text-sm text-gray-700">Highlight terms</span>
            <button
              onClick={() => onToggleHighlighting(!highlightingEnabled)}
              className={cn(
                "relative inline-flex h-6 w-11 items-center rounded-full transition-colors",
                highlightingEnabled ? "bg-blue-600" : "bg-gray-300"
              )}
            >
              <span
                className={cn(
                  "inline-block h-4 w-4 transform rounded-full bg-white transition-transform",
                  highlightingEnabled ? "translate-x-6" : "translate-x-1"
                )}
              />
            </button>
          </div>
        )}
        
        {/* Search */}
        <div className="relative">
          <input
            type="text"
            placeholder="Search terms..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        
        <div className="mt-2 text-xs text-gray-500">
          {filteredTerms.length} term{filteredTerms.length !== 1 ? 's' : ''} found
        </div>
      </div>

      {/* Terms List */}
      <div className="flex-1 overflow-y-auto">
        {filteredTerms.length === 0 ? (
          <div className="p-4 text-center text-gray-500">
            <BookOpen className="w-8 h-8 mx-auto mb-2 text-gray-300" />
            <p className="text-sm">No technical terms found</p>
            <p className="text-xs text-gray-400 mt-1">
              Terms will appear here as you read the content
            </p>
          </div>
        ) : (
          <div className="p-2">
            {filteredTerms.map((term) => {
              const isExpanded = expandedTerms.has(term.definition.id);
              const categoryColor = getCategoryColor(term.definition.category);
              
              return (
                <div
                  key={term.definition.id}
                  className="mb-2 border border-gray-200 rounded-lg overflow-hidden"
                >
                  {/* Term Header */}
                  <button
                    onClick={() => toggleTermExpansion(term.definition.id)}
                    className="w-full p-3 text-left hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-2 mb-1">
                          <span className="font-medium text-gray-900 truncate">
                            {term.definition.word}
                          </span>
                          {term.frequency > 1 && (
                            <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">
                              {term.frequency}x
                            </span>
                          )}
                        </div>
                        {term.definition.category && (
                          <span className={`text-xs px-2 py-0.5 rounded-full ${categoryColor}`}>
                            {term.definition.category}
                          </span>
                        )}
                      </div>
                      {isExpanded ? (
                        <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      ) : (
                        <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      )}
                    </div>
                  </button>

                  {/* Term Details */}
                  {isExpanded && (
                    <div className="px-3 pb-3 border-t border-gray-100">
                      <div className="pt-3">
                        {/* Image if available */}
                        {term.definition.image && (
                          <div className="mb-3">
                            <img
                              src={term.definition.image}
                              alt={term.definition.word}
                              className="w-full h-32 object-cover rounded-lg border border-gray-200"
                              onError={(e) => {
                                // Hide image if it fails to load
                                (e.target as HTMLImageElement).style.display = 'none';
                              }}
                            />
                          </div>
                        )}
                        
                        <p className="text-sm text-gray-700 mb-3">
                          {term.definition.explanation}
                        </p>
                        
                        {term.definition.aliases && term.definition.aliases.length > 0 && (
                          <div className="mb-3">
                            <p className="text-xs font-medium text-gray-500 mb-1">Also known as:</p>
                            <div className="flex flex-wrap gap-1">
                              {term.definition.aliases.map((alias, index) => (
                                <span
                                  key={index}
                                  className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded"
                                >
                                  {alias}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {term.context && (
                          <div>
                            <p className="text-xs font-medium text-gray-500 mb-1">Context:</p>
                            <p className="text-xs text-gray-600 italic bg-gray-50 p-2 rounded">
                              "...{term.context}..."
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default TechnicalTermsSidebar;
